/**
 * TTS（テキスト読み上げ）composable
 *
 * バックエンドの /tts/ エンドポイントを呼び出し、音声（MP3）を再生する。
 * バックエンドが 204 を返した場合（クォータ超過 / tts_provider 未設定）、
 * または ttsApi 自体が未設定の場合はブラウザ標準 TTS（Web Speech API）にフォールバックする。
 *
 * キャッシュ:
 *   モジュールスコープの Map（SHA-256 ハッシュ → ObjectURL）で管理。
 *   同じテキストへの 2 回目以降のクリックは API を呼ばず即再生（Google TTS 経由時のみ）。
 *   ブラウザ TTS はキャッシュなし。ページリロードでクリア。
 *
 * 状態はモジュールスコープで共有し、複数コンポーネントが同じ ref を参照できる。
 */

const ttsCache = new Map<string, string>() // textHash → objectURL

const isSpeaking = ref(false)
const speakingId = ref<string>("")
const isLoading = ref(false)

let currentAudio: HTMLAudioElement | null = null

/** SHA-256 ハッシュ（小文字 16 進数） */
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest("SHA-256", data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
}

/** HTML タグ・エンティティを除去してプレーンテキストに変換 */
function stripHtml(html: string): string {
  return html
     .replace(/<br\s*\/?>/gi, "。")
     .replace(/<sup[^>]*>[\s\S]*?<\/sup>/gi, "") // 引用番号（上付き文字）を除去
     .replace(/<\/h[1-6]>/gi, "。")
     .replace(/<\/p>/gi, "。")
     .replace(/<li[^>]*>/gi, "・")
     .replace(/<\/li>/gi, "。")
     .replace(/<[^>]+>/g, "")
     .replace(/。{2,}/g, "。")
     .replace(/&nbsp;/g, " ")
     .replace(/&lt;/g, "<")
     .replace(/&gt;/g, ">")
     .replace(/&amp;/g, "&")
     .replace(/&quot;/g, '"')
    .trim()
}

function stopCurrent() {
  if (currentAudio) {
    currentAudio.pause()
    currentAudio.src = ""
    currentAudio = null
  }
  if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
    window.speechSynthesis.cancel()
  }
  isSpeaking.value = false
  speakingId.value = ""
}

/** ブラウザ標準 TTS（Web Speech API）で読み上げる */
function _speakBrowser(text: string, lang: string, id: string) {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    console.warn("[useTTS] Web Speech API not supported")
    isSpeaking.value = false
    speakingId.value = ""
    return
  }
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = lang
  speakingId.value = id
  isSpeaking.value = true
  utterance.onend = () => {
    isSpeaking.value = false
    speakingId.value = ""
  }
  utterance.onerror = () => {
    isSpeaking.value = false
    speakingId.value = ""
  }
  window.speechSynthesis.speak(utterance)
}

export function useTTS() {
  const config = useRuntimeConfig()

  /**
   * テキストを読み上げる。
   * @param htmlText   読み上げるテキスト（HTML タグ含んでもよい）
   * @param lang       言語コード（例: "ja", "en"）
   * @param id         このボタン固有の ID（historyId など）
   */
  async function speak(htmlText: string, lang: string, id: string) {
    const text = stripHtml(htmlText)
    if (!text) return

    // 同じ ID が再生中ならトグル停止
    if (speakingId.value === id && isSpeaking.value) {
      stopCurrent()
      return
    }

    // 別の ID が再生中なら停止して切り替え
    stopCurrent()
    speakingId.value = id

    const ttsApi: string = config.public.ttsApi as string
    if (!ttsApi) {
      // ttsApi 未設定 → ブラウザ TTS
      _speakBrowser(text, lang, id)
      return
    }

    // キャッシュ確認（Google TTS 経由時のみ有効）
    const hash = await sha256(text)
    const cached = ttsCache.get(hash)
    if (cached) {
      _playObjectUrl(cached, id)
      return
    }

    // API コール
    isLoading.value = true
    try {
      const res = await fetch(ttsApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, lang }),
      })

      if (res.status === 204) {
        // クォータ超過 or tts_provider 未設定 → ブラウザ TTS にフォールバック
        _speakBrowser(text, lang, id)
        return
      }

      if (!res.ok) {
        const detail = await res.text().catch(() => "")
        console.error("[useTTS] API error:", res.status, detail)
        isSpeaking.value = false
        speakingId.value = ""
        return
      }

      const arrayBuffer = await res.arrayBuffer()
      const blob = new Blob([arrayBuffer], { type: "audio/mpeg" })
      const objectUrl = URL.createObjectURL(blob)

      ttsCache.set(hash, objectUrl)
      _playObjectUrl(objectUrl, id)
    } catch (e) {
      console.error("[useTTS] fetch error:", e)
      isSpeaking.value = false
      speakingId.value = ""
    } finally {
      isLoading.value = false
    }
  }

  function _playObjectUrl(objectUrl: string, id: string) {
    const audio = new Audio(objectUrl)
    currentAudio = audio

    audio.onended = () => {
      isSpeaking.value = false
      speakingId.value = ""
      currentAudio = null
    }

    audio.onerror = () => {
      isSpeaking.value = false
      speakingId.value = ""
      currentAudio = null
    }

    audio
      .play()
      .then(() => {
        isSpeaking.value = true
        speakingId.value = id
      })
      .catch((e) => {
        console.error("[useTTS] play error:", e)
        isSpeaking.value = false
        speakingId.value = ""
        currentAudio = null
      })
  }

  function stop() {
    stopCurrent()
  }

  return { isSpeaking, speakingId, isLoading, speak, stop }
}
